import * as sillyname from 'sillyname';
import {
  IAreaChannelAllowed,
  TO_LOUNGE_FROM_TWITCH_VOLUME,
} from '../components/party/AppConstants';
import { EAreaPrefix, IPartyForUser, TAreaKey } from '../PartyClientResponse';
import { IArea } from '../Party';
import { $enum } from 'ts-enum-util';
import { ReactComponent as BarIcon } from '../components/party/icons/Icon_bar1_white.svg';
import { ReactComponent as LobbyIcon } from '../components/party/icons/home_lobby_.svg';
import { ReactComponent as PartyIcon } from '../components/party/icons/Icon_dancefloor2_white.svg';
import React from 'react';
import LobbyText from '../components/party/LobbyText';

const fakeUserAreaNames = new Map<TAreaKey, string>();

// Fake users are client-side thus won't have an entry in the areas object
// Assume that if a room doesn't have a name, then a fake user created it and give it a silly one
// storing it here so it's not constantly regenerating a new name every render
const getAreaNameCreatedByFakeUser = (areaKey: TAreaKey): string => {
  if (
    $enum(EAreaPrefix)
      .getValues()
      .map((val) => `${val}-0`)
      .includes(areaKey)
  ) {
    return getAreaFeatures(areaKey).title;
  }

  if (!fakeUserAreaNames.get(areaKey)) {
    const name = `${sillyname().split(' ')[0]}'s lounge`;
    fakeUserAreaNames.set(areaKey, name);
    return name;
  }
  return fakeUserAreaNames.get(areaKey)!;
};

type TAreaFeatureMap = {
  [partyType in EAreaPrefix]: IAreaFeatures;
};

interface IAreaFeatures {
  title: string;
  description: string;
  icon: React.FunctionComponent<
    React.SVGProps<SVGSVGElement> & {
      title?: string | undefined;
    }
  >;
  background: string;
  streamVolume: number;
  audio: IAreaChannelAllowed;
  video: IAreaChannelAllowed;
  videoHeightPercent: number;
  shouldShowArea: boolean;
  roomText: React.FunctionComponent | null;
}

const PARTY_TYPE_FEATURE_MAP: TAreaFeatureMap = {
  // [EAreaPrefix.ChillPrefix]: {
  //   title: 'The Dark Room',
  //   description: 'No video, no talk, just the music.',
  //   background: `linear-gradient(rgba(0, 0, 0, 0.8), rgba(0, 0, 0, 0.8)), url('https://images.unsplash.com/photo-1568973290903-850cb66e92c9?ixlib=rb-1.2.1&ixid=eyJhcHBfaWQiOjEyMDd9&auto=format&fit=crop&w=2250&q=80')`,
  //   icon: require('../components/party/icons/chill.svg'),
  //   streamVolume: 1,
  //   audio: 'muted',
  //   video: 'muted',
  // },
  [EAreaPrefix.PartyPrefix]: {
    title: 'Dancefloor',
    description: 'No mic, video to see and be seen, music ON.',
    background: `linear-gradient(rgba(0, 0, 0, 0.8), rgba(0, 0, 0, 0.8)), url('https://images.unsplash.com/photo-1519111887837-a48ccf9edc00?ixlib=rb-1.2.1&ixid=eyJhcHBfaWQiOjEyMDd9&auto=format&fit=crop&w=2250&q=80')`,
    // icon: './icons/Icon_dancefloor2_white.svg',
    icon: PartyIcon,

    streamVolume: 1,
    audio: 'muted',
    video: 'allowed',
    videoHeightPercent: 70,
    shouldShowArea: true,
    roomText: null,
  },
  [EAreaPrefix.ChatPrefix]: {
    title: 'Bar',
    description: 'Mic on for chit chat, video on to see your friends, background music.',
    background: `linear-gradient(rgba(0, 0, 0, 0.8), rgba(0, 0, 0, 0.8)), url('https://images.unsplash.com/photo-1560290881-c6839ff0717e?ixlib=rb-1.2.1&ixid=eyJhcHBfaWQiOjEyMDd9&auto=format&fit=crop&w=2167&q=80')`,
    icon: BarIcon,
    streamVolume: 1,
    audio: 'allowed',
    video: 'allowed',
    videoHeightPercent: 60,
    shouldShowArea: true,
    roomText: null,
  },
  [EAreaPrefix.LobbyPrefix]: {
    title: 'Lobby',
    description: 'Just here for the show. No webcam or mics allowed.',
    background: `linear-gradient(rgba(0, 0, 0, 0.8), rgba(0, 0, 0, 0.8)), url('https://images.unsplash.com/photo-1560290881-c6839ff0717e?ixlib=rb-1.2.1&ixid=eyJhcHBfaWQiOjEyMDd9&auto=format&fit=crop&w=2167&q=80')`,
    icon: LobbyIcon,
    streamVolume: 1,
    audio: 'muted',
    video: 'muted',
    videoHeightPercent: 80,
    shouldShowArea: false,
    roomText: null,
  },
};

export const getAreaFeatures = (areaKey: TAreaKey) => {
  const features =
    Object.entries(PARTY_TYPE_FEATURE_MAP)
      .filter(([k]) => areaKey.includes(k))
      .map(([, v]) => v)[0] || PARTY_TYPE_FEATURE_MAP[EAreaPrefix.PartyPrefix];

  return {
    ...features,
    needsAV: avRequiredStates.includes(features.audio) || avRequiredStates.includes(features.video),
  };
};

const avRequiredStates: IAreaChannelAllowed[] = ['allowed', 'mandatory'];

export interface IAreaInfo extends IArea, IAreaFeatures {
  needsAV: boolean;
}

export const getAreaInfo = (areaKey: TAreaKey, party: IPartyForUser): IAreaInfo => {
  const features = getAreaFeatures(areaKey);
  const area = (party.areas || {})[areaKey] || {};
  return {
    ...features,
    ...area,
    name: area?.name || getAreaNameCreatedByFakeUser(areaKey),
    key: areaKey,
    settings: {
      iconUrl: area?.settings?.iconUrl || '',
      private: area?.settings?.private || false,
      allowedParticipants: area?.settings?.allowedParticipants,
    },
  };
};

export const getNextRoomId = (prefix: string) =>
  `${prefix}-${Math.round(Math.random() * 1000000)}` as TAreaKey;
